import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { toFinite } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { z } from 'zod';

export type ReportsCompanyRevenueShipperDTO = z.infer<
  typeof reportsCompanyRevenueShipperSchema
>;
const reportsCompanyRevenueShipperSchema = z.object({
  company_name: z.string().catch(''),
  order_count: z.preprocess(
    (value) => toFinite(value),
    z.number().default(0).catch(0),
  ),
  revenue: z.preprocess(
    (value) => toFinite(value),
    z.number().default(0).catch(0),
  ),
});

export type ReportsCompanyRevenueDTO = z.infer<
  typeof reportsCompanyRevenueSchema
>;
export const reportsCompanyRevenueSchema = z.object({
  total: z.preprocess(
    (value) => (value == null ? value : Number(value)),
    z.number().default(0).catch(0),
  ),
  shippers: z.array(reportsCompanyRevenueShipperSchema).prefault([]),
});

export const ACCOUNTING_METHOD = ['cash', 'accrual'] as const;
export type AccountingMethod = (typeof ACCOUNTING_METHOD)[number];

export type ReportsCompanyRevenueParamsDTO = z.infer<
  typeof reportsCompanyRevenueParamsSchema
>;
/** The date defaults are thunks so that they are re-evaluated on every parse. */
export const reportsCompanyRevenueParamsSchema = z.object({
  start_date: z
    .preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    )
    .default(() => stringifyDate(DateTime.local().startOf('month'))),
  end_date: z
    .preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    )
    .default(() => stringifyDate(DateTime.local().endOf('day'))),
  accounting_method: z.enum(ACCOUNTING_METHOD).catch('cash').default('cash'),
});

export type ReportsPeriodDTO = z.infer<typeof reportsPeriodSchema>;
export const reportsPeriodSchema = z.object({
  start_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null).catch(null),
  ),
  end_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null).catch(null),
  ),
});

export type ReportsOverviewParamsDTO = z.infer<
  typeof reportsOverviewParamsSchema
>;
export const reportsOverviewParamsSchema = reportsPeriodSchema.extend({
  delivery_trend_tab: z.preprocess(
    (value) => (value == null ? null : String(value).trim() || null),
    z.string().nullable().default(null).catch(null),
  ),
  ranking_tab: z.preprocess(
    (value) => (value == null ? null : String(value).trim() || null),
    z.string().nullable().default(null).catch(null),
  ),
  invoice_status_tab: z.preprocess(
    (value) => (value == null ? null : String(value).trim() || null),
    z.string().nullable().default(null).catch(null),
  ),
});

export type ReportsFiltersValue = Record<string, string[]>;

function normalizeReportsFilterValue(
  value: string | string[] | null | undefined,
): string[] | null {
  if (value == null) return null;

  if (typeof value === 'string') {
    return value ? [value] : [];
  }

  if (!Array.isArray(value)) return null;

  const items = value.filter(
    (item): item is string => typeof item === 'string',
  );
  if (items.length !== value.length) return null;

  return items;
}

function getReportsFilters(
  params: Record<string, string | string[] | null | undefined>,
  reservedKeys: readonly string[],
): ReportsFiltersValue {
  const filters: ReportsFiltersValue = {};

  for (const [key, value] of Object.entries(params)) {
    if (reservedKeys.includes(key)) continue;

    const normalized = normalizeReportsFilterValue(value);
    if (normalized == null) continue;

    filters[key] = normalized;
  }

  return filters;
}

export function buildActiveFilters(
  filters: ReportsFiltersValue = {},
): ReportsFiltersValue {
  return Object.entries(filters).reduce<ReportsFiltersValue>(
    (activeFilters, [key, values]) => {
      if (values.length > 0) {
        activeFilters[key] = values;
      }

      return activeFilters;
    },
    {},
  );
}

export type ReportsRevenueFiltersValue = ReportsFiltersValue;

export const RESERVED_REVENUE_PARAM_KEYS = [
  'start_date',
  'end_date',
  'load_details_sort',
  'shippers_tab',
  'drivers_tab',
  'filters',
] as const;

export interface ReportsRevenueParamsDTO extends ReportsPeriodDTO {
  load_details_sort?: string | null;
  shippers_tab?: string | null;
  drivers_tab?: string | null;
  [filterKey: string]: string | string[] | null | undefined;
}
/**
 * The `catchall` keeps the arbitrary dashboard filter keys that
 * {@link getReportsRevenueFilters} reads.
 */
export const reportsRevenueParamsSchema = z
  .object({
    start_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    end_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    load_details_sort: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
    shippers_tab: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
    drivers_tab: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
  })
  .catchall(z.custom<string | string[] | null | undefined>());

export function getReportsRevenueFilters(
  params: ReportsRevenueParamsDTO,
): ReportsRevenueFiltersValue {
  return getReportsFilters(params, RESERVED_REVENUE_PARAM_KEYS);
}

export type ReportsInvoicesFiltersValue = ReportsFiltersValue;

export const RESERVED_INVOICES_PARAM_KEYS = [
  'start_date',
  'end_date',
  'invoices_sort',
  'filters',
] as const;

export interface ReportsInvoicesParamsDTO extends ReportsPeriodDTO {
  invoices_sort?: string | null;
  [filterKey: string]: string | string[] | null | undefined;
}

export type ReportsExpensesFiltersValue = ReportsFiltersValue;

export const RESERVED_EXPENSES_PARAM_KEYS = [
  'start_date',
  'end_date',
  'expense_details_sort',
  'filters',
] as const;

export interface ReportsExpensesParamsDTO extends ReportsPeriodDTO {
  expense_details_sort?: string | null;
  [filterKey: string]: string | string[] | null | undefined;
}

/** Passthrough params — see {@link reportsRevenueParamsSchema}. */
export const reportsExpensesParamsSchema = z
  .object({
    start_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    end_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    expense_details_sort: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
  })
  .catchall(z.custom<string | string[] | null | undefined>());

export function getReportsExpensesFilters(
  params: ReportsExpensesParamsDTO,
): ReportsExpensesFiltersValue {
  return getReportsFilters(params, RESERVED_EXPENSES_PARAM_KEYS);
}

/** Passthrough params — see {@link reportsRevenueParamsSchema}. */
export const reportsInvoicesParamsSchema = z
  .object({
    start_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    end_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    invoices_sort: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
  })
  .catchall(z.custom<string | string[] | null | undefined>());

export function getReportsInvoicesFilters(
  params: ReportsInvoicesParamsDTO,
): ReportsInvoicesFiltersValue {
  return getReportsFilters(params, RESERVED_INVOICES_PARAM_KEYS);
}

export type ReportsDeliveryTrendFiltersValue = ReportsFiltersValue;

export const RESERVED_DELIVERY_TREND_PARAM_KEYS = [
  'start_date',
  'end_date',
  'delivery_trend_tab',
  'delivery_details_sort',
  'filters',
] as const;

export interface ReportsDeliveryTrendParamsDTO extends ReportsPeriodDTO {
  delivery_trend_tab?: string | null;
  delivery_details_sort?: string | null;
  [filterKey: string]: string | string[] | null | undefined;
}

/** Passthrough params — see {@link reportsRevenueParamsSchema}. */
export const reportsDeliveryTrendParamsSchema = z
  .object({
    start_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    end_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    ),
    delivery_trend_tab: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
    delivery_details_sort: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null)
      .transform((value) => value || null),
  })
  .catchall(z.custom<string | string[] | null | undefined>());

export function getReportsDeliveryTrendFilters(
  params: ReportsDeliveryTrendParamsDTO,
): ReportsDeliveryTrendFiltersValue {
  return getReportsFilters(params, RESERVED_DELIVERY_TREND_PARAM_KEYS);
}

export type ReportsDriverPayDTO = z.infer<typeof driverPaySchema>;
// No `min`/`max` on `pay_rate`: nothing validates a query response, and Zod
// would enforce them during `cast`, replacing an out-of-range `pay_rate` with
// the default instead of rendering it.
export const driverPaySchema = z.object({
  guid: z.string().catch(''),
  name: z.string().catch(''),
  email: z.string().catch(''),
  pay_rate: z.preprocess(
    (value) => (value == null ? value : Number(value)),
    z.number().default(0).catch(0),
  ),
  revenue: z.preprocess(
    (value) => (value == null ? value : Number(value)),
    z.number().default(0).catch(0),
  ),
});

export type ReportsAccountsPayableShipperDTO = z.infer<
  typeof accountsPayableShipperSchema
>;
const accountsPayableShipperSchema = z.object({
  company_name: z.string().catch(''),
  order_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  amount: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export type ReportsAccountsPayableDTO = z.infer<typeof accountsPayableSchema>;
export const accountsPayableSchema = reportsPeriodSchema.extend({
  total: z
    .preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    )
    .default(0),
  shippers: z.array(accountsPayableShipperSchema).prefault([]),
});

export type ReportsDriverPayQueryParamsDTO = z.infer<
  typeof driverPayQueryParamsSchema
>;
const driverPayQueryParamsSchema = reportsPeriodSchema.extend({
  /** A missing param becomes `false`, an unparsable one becomes `null`. */
  only_active: z
    .preprocess((value) => {
      if (typeof value === 'boolean') {
        return value;
      }

      if (/^(true|1)$/i.test(String(value))) {
        return true;
      }

      return /^(false|0)$/i.test(String(value)) ? false : null;
    }, z.boolean().nullable().catch(null))
    .default(false),
});

export type ReportsDriverPayParamsDTO = z.infer<typeof driverPayParamsSchema>;
/** Period defaults are thunks — see {@link reportsCompanyRevenueParamsSchema}. */
export const driverPayParamsSchema = driverPayQueryParamsSchema.extend({
  /** Both clamp to a positive integer the same way `loads` does. */
  page: z.preprocess((value) => {
    const next = Number(value ?? '');

    return Number.isFinite(next) && next > 0
      ? Math.max(1, Math.round(next))
      : 1;
  }, z.number().default(1).catch(1)),
  page_size: z.preprocess((value) => {
    const next = Number(value ?? '');

    return Number.isFinite(next) && next > 0
      ? Math.max(1, Math.round(next))
      : 20;
  }, z.number().default(20).catch(20)),
  start_date: z
    .preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    )
    .default(() => stringifyDate(DateTime.local().startOf('month'))),
  end_date: z
    .preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null).catch(null),
    )
    .default(() => stringifyDate(DateTime.local().endOf('day'))),
});

export type DriverPayTotalReportDTO = z.infer<
  typeof driverPayTotalReportSchema
>;
export const driverPayTotalReportSchema = z.object({
  total_revenue: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  total_fee: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export const customReportFilters = {
  loadTypeFilters: new Map([
    ['all', 'All Loads'],
    ['active', 'Active Loads'],
    ['archived', 'Archived Loads'],
  ]),
  loadStatusFilters: new Map([
    ['all', 'All'],
    ['new', 'New'],
    ['picked_up', 'Picked Up'],
    ['delivered', 'Delivered'],
  ]),
  orderByFilters: new Map([
    ['created_at', 'Creation Date'],
    ['delivery_date', 'Actual Delivery Date'],
  ]),
};

export const customReportFields = {
  generalInfo: new Map([
    ['number', 'Load ID'],
    ['internal_load_id', 'Internal Load ID'],
    ['created_at', 'Creation Date'],
    ['dispatcher', 'Dispatcher'],
    ['trip', 'Trip'],
    ['has_terminal', 'Has Terminal'],
    ['is_dispatched_to_carrier', 'Dispatched to Carrier'],
  ]),
  vehicleInfo: new Map([
    ['vehicle_info', 'Vehicle Year, Make, Model'],
    ['vin', 'VIN'],
    ['lot_number', 'Lot Number'],
  ]),
  pickupInfo: new Map([
    ['pickup_name', 'Pickup Name'],
    ['pickup_address', 'Pickup Address'],
    ['pickup_date', 'Scheduled Pickup Date'],
    ['pickup_time', 'Scheduled Pickup Time'],
    ['pickedup_date', 'Actual Pickup Date'],
    ['pickedup_time', 'Actual Pickup Time'],
    ['buyer_number', 'Buyer Number'],
    ['pickup_notes', 'Pickup Notes'],
    ['pickup_phone', 'Pickup Phone'],
    ['pickup_phone2', 'Pickup Mobile'],
  ]),
  deliveryInfo: new Map([
    ['delivery_name', 'Delivery Name'],
    ['delivery_address', 'Delivery Address'],
    ['delivery_date', 'Scheduled Delivery Date'],
    ['delivery_time', 'Scheduled Delivery Time'],
    ['delivered_date', 'Actual Delivery Date'],
    ['delivered_time', 'Actual Delivery Time'],
    ['delivery_notes', 'Delivery Notes'],
    ['delivery_phone', 'Delivery Phone'],
    ['delivery_phone2', 'Delivery Mobile'],
  ]),
  brokerInfo: new Map([
    ['broker_name', 'Shipper Name'],
    ['broker_phone', 'Shipper Phone'],
  ]),
  driverInfo: new Map([['driver', 'Driver Name']]),
  payment: new Map([
    ['price', 'Price'],
    ['expenses', 'Expenses'],
    ['broker_fee', 'Broker Fee'],
    ['driver_pay', 'Driver Pay'],
    ['payment_method', 'Payment Method'],
    ['paid_amount', 'Paid Amount'],
    ['paid_method', 'Paid Method'],
    ['payment_reference_number', 'Reference Number'],
    ['receipt_date', 'Receipt Date'],
    ['payment_notes', 'Payment Notes'],
    ['payment_terms', 'Payment Terms'],
    ['payment_status', 'Payment Status'],
    ['invoice_id', 'Invoice Number'],
    ['invoice_date', 'Invoice Date'],
    ['invoice_notes', 'Invoice Notes'],
    ['instant_transfer_fee', 'Instant Transfer Fee'],
  ]),
};

export type ReportsCustomDTO = z.infer<typeof customReportSchema>;
export const customReportSchema = z.object({
  creation_period: reportsPeriodSchema.nullable().default(null),
  delivery_period: reportsPeriodSchema.nullable().default(null),
  drivers: z.array(z.string().catch('')).default([]),
  dispatchers: z.array(z.string().catch('')).default([]),
  fields: z.array(z.string().catch('')).default([]),
  guid: z.string().catch(''),
  include_not_assigned: z.preprocess(
    (value) =>
      typeof value === 'string' || typeof value === 'number'
        ? /^(true|1)$/i.test(String(value))
        : value,
    z.boolean().nullable().default(false).catch(false),
  ),
  include_removed_drivers: z.preprocess(
    (value) =>
      typeof value === 'string' || typeof value === 'number'
        ? /^(true|1)$/i.test(String(value))
        : value,
    z.boolean().nullable().default(false).catch(false),
  ),
  load_filter: z
    .enum(['all', 'active', 'archived'])
    .catch('all')
    .default('all'),
  load_status: z
    .enum(['all', 'new', 'picked_up', 'delivered'])
    .catch('all')
    .default('all'),
  name: z.string().catch(''),
  order_by: z
    .enum(['created_at', 'delivery_date'])
    .catch('created_at')
    .default('created_at'),
  report_email: z.string().catch(''),
});

export type SendDriverPayReportRequestDTO = z.infer<
  typeof sendDriverPayReportRequestSchema
>;
/**
 * The email rules live in `superRefine` rather than on the field, so a failed
 * check reports an error instead of making the cast fall back to the default.
 */
export const sendDriverPayReportRequestSchema = driverPayQueryParamsSchema
  .extend({
    email: z.string().catch(''),
  })
  .superRefine(({ email }, ctx) => {
    if (!email) {
      ctx.addIssue({
        code: 'custom',
        path: ['email'],
        message: 'This field is required',
      });
      return;
    }

    if (!z.email().safeParse(email).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['email'],
        message: 'Enter a valid email',
      });
    }
  });

/** Email rules live in `superRefine` — see {@link sendDriverPayReportRequestSchema}. */
export const reportsRevenueSendReportSchema = reportsPeriodSchema
  .extend({
    emails: z.array(z.string().catch('')).default([]).catch([]),
    apply_filters: z.boolean().nullable().default(true).catch(true),
    selections: z.record(z.string(), z.array(z.string())).default({}).catch({}),
  })
  .superRefine(({ emails }, ctx) => {
    if (emails.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['emails'],
        message: 'Add at least one recipient email',
      });
      return;
    }

    emails.forEach((email, index) => {
      if (!email) {
        ctx.addIssue({
          code: 'custom',
          path: ['emails', index],
          message: 'This field is required',
        });
        return;
      }

      if (!z.email().safeParse(email).success) {
        ctx.addIssue({
          code: 'custom',
          path: ['emails', index],
          message: 'Enter a valid email',
        });
      }
    });
  });

export interface SendRevenueReportRequestDTO {
  recipients: string[];
  filters: DashboardDataFilters;
}

export type ReportsAccountsReceivableShipperDTO = z.infer<
  typeof reportsAccountsReceivableShipperSchema
>;
const reportsAccountsReceivableShipperSchema = z.object({
  company_name: z.string().catch(''),
  total_due_order_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  total_due_amount: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  past_due_order_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  past_due_amount: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export type ReportsAccountsReceivableDTO = z.infer<
  typeof reportsAccountsReceivableSchema
>;
export const reportsAccountsReceivableSchema = reportsPeriodSchema.extend({
  current_due: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  past_due: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  total_due: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  shippers: z.array(reportsAccountsReceivableShipperSchema).prefault([]),
  shippers_with_missing_info: z
    .array(reportsAccountsReceivableShipperSchema)
    .prefault([]),
});

export type DashboardListItemDTO = z.infer<typeof dashboardListItemSchema>;
export const dashboardListItemSchema = z.object({
  id: z.string().catch(''),
  key: z.string().catch(''),
  name: z.string().catch(''),
  kind: z.string().catch(''),
  platform: z.string().catch(''),
  required_tier: z.string().catch(''),
  tier_locked: z.preprocess(
    (value) =>
      typeof value === 'string' || typeof value === 'number'
        ? /^(true|1)$/i.test(String(value))
        : value,
    z.boolean().nullable().default(false).catch(false),
  ),
  paywall_reason: z.string().catch(''),
});

const dashboardWidgetTabSchema = z.object({
  key: z.string().catch(''),
  label: z.string().catch(''),
});

const dashboardWidgetSchema = z.object({
  id: z.string().catch(''),
  widget_id: z.string().catch(''),
  key: z.string().catch(''),
  name: z.string().catch(''),
  kind: z.string().catch(''),
  size: z.string().catch(''),
  sort_order: z
    .preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    )
    .default(0),
  required_tier: z.string().catch(''),
  required_permission: z.string().catch(''),
  /** An explicit `null` becomes `[]`, a missing value falls back to `null`. */
  tabs: z.preprocess(
    (value) => (value === null ? [] : value),
    z.array(dashboardWidgetTabSchema).nullable().default(null),
  ),
  /** Stays `null` so `useOverviewWidget` can fall back to the first tab. */
  default_tab: z.string().nullable().catch(null),
  sortable_columns: z.array(z.string().catch('')).default([]),
  /** An identity passthrough — the config shape is widget-specific. */
  config: z.custom<Record<string, unknown>>(() => true).default({}),
});

export type DashboardFilterDTO = z.infer<typeof dashboardFilterSchema>;
const dashboardFilterSchema = z.object({
  key: z.string().catch(''),
  label: z.string().catch(''),
  multi: z.preprocess(
    (value) =>
      typeof value === 'string' || typeof value === 'number'
        ? /^(true|1)$/i.test(String(value))
        : value,
    z.boolean().nullable().default(false).catch(false),
  ),
});

export type DashboardDetailDTO = z.infer<typeof dashboardDetailSchema>;
export const dashboardDetailSchema = z.object({
  id: z.string().catch(''),
  key: z.string().catch(''),
  name: z.string().catch(''),
  kind: z.string().catch(''),
  platform: z.string().catch(''),
  required_tier: z.string().catch(''),
  date_range_max_days: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  widgets: z.array(dashboardWidgetSchema).default([]),
  filters: z.array(dashboardFilterSchema).default([]),
});

export type WidgetCell = string | number | boolean | null;

export interface WidgetColumn {
  name: string;
  type: string;
}

const widgetColumnSchema = z.object({
  name: z.string().catch(''),
  type: z.string().catch(''),
});

/**
 * An unexpected cell degrades to `null` instead of `undefined`, which would
 * corrupt the rendered table.
 */
const widgetCellSchema = z
  .union([z.string(), z.number(), z.boolean(), z.null()])
  .catch(null);

export type DashboardWidgetData = z.infer<typeof dashboardWidgetDataSchema>;
const dashboardWidgetDataSchema = z.object({
  id: z.string().catch(''),
  key: z.string().catch(''),
  status: z.string().catch(''),
  active_tab: z.string().catch(''),
  freshness: z.string().catch(''),
  data: z
    .object({
      columns: z.array(widgetColumnSchema).default([]),
      rows: z.array(z.array(widgetCellSchema)).default([]),
    })
    .nullable()
    .prefault({}),
});

const dashboardDataDateRangeSchema = z.object({
  preset: z.string().catch(''),
  start_date: z.string().catch(''),
  end_date: z.string().catch(''),
  clamped: z.preprocess(
    (value) =>
      typeof value === 'string' || typeof value === 'number'
        ? /^(true|1)$/i.test(String(value))
        : value,
    z.boolean().nullable().default(false).catch(false),
  ),
});

export type DashboardDataDTO = z.infer<typeof dashboardDataSchema>;
export const dashboardDataSchema = z.object({
  dashboard_id: z.string().catch(''),
  updated_at: z.string().catch(''),
  applied_filters: z
    .object({
      date_range: dashboardDataDateRangeSchema.nullable().default(null),
    })
    .nullable()
    .prefault({}),
  widgets: z.array(dashboardWidgetDataSchema).default([]),
});

export interface DashboardDataFilters {
  date_range?: {
    preset?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };
  selections?: Record<string, string[]>;
}

export interface DashboardFilterOption {
  value: string;
  label: string;
}

export type DashboardFilterOptionsMap = Record<string, DashboardFilterOption[]>;

/**
 * The API shape is not pinned down and this feeds every filter dropdown, so the
 * schema stays permissive.
 */
export const dashboardFilterOptionsSchema = z
  .custom<DashboardFilterOptionsMap>(() => true)
  .default({});

export const DASHBOARD_KEYS = {
  OVERVIEW: 'carrier_overview',
  REVENUE: 'carrier_revenue',
  EXPENSES: 'carrier_expenses',
  DELIVERY_TREND: 'carrier_delivery',
  INVOICES: 'carrier_invoices',
};

export const WIDGET_KINDS = {
  KPI_KIND: 'kpi_card',
};

export const DASHBOARD_WIDGET_KEYS = {
  REVENUE: 'carrier_revenue_overview',
  REVENUE_KPIS: 'carrier_revenue_kpis',
  REVENUE_BY_SHIPPERS: 'carrier_revenue_by_shippers',
  REVENUE_BY_DRIVERS: 'carrier_revenue_by_drivers',
  EXPENSES: 'carrier_expenses_overview',
  EXPENSE_DETAILS: 'carrier_expenses_details',
  DELIVERY_LOAD_DETAILS: 'carrier_delivery_load_details',
  DELIVERY_TREND: 'carrier_delivery_trend',
  DELIVERY_BOOKED_DELIVERED_TREND: 'carrier_delivery_booked_delivered_trend',
  CARRIER_RANKING: 'carrier_revenue_rankings',
  INVOICE_STATUS: 'carrier_invoice_status',
  REVENUE_TREND: 'carrier_revenue_trend',
  REVENUE_LOAD_DETAILS: 'carrier_revenue_load_details',
  EXPENSES_BY_TYPE: 'carrier_expenses_by_type',
  EXPENSES_TREND: 'carrier_expenses_trend',
  EXPENSE_TABLE: 'carrier_expense_level_details',
  INVOICE_KPIS: 'carrier_invoices_kpis',
  INVOICE_DETAILS: 'carrier_invoices_details',
  CARRIER_DELIVERY_KPIS: 'carrier_delivery_kpis',
};
